import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { readRuntimeConfig } from '../config/runtime-config';
import { ApiError, type ApiProblemDetails } from './types';
import { notifyAuthenticationRequired } from '@nwm-oss/auth-react/authentication-required';

const ACTIVE_ORGANIZATION_STORAGE_KEY = 'nwm.active-organization-id';

// Token accessor is set by the AuthProvider once it initialises.
// This avoids a circular dependency between the API client and auth context.
let _getToken: (() => string | null) | null = null;

export function registerTokenAccessor(fn: () => string | null): void {
  _getToken = fn;
}

export function readActiveOrganizationId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage.getItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeActiveOrganizationId(organizationId: string | null | undefined): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (organizationId && organizationId.trim()) {
      window.sessionStorage.setItem(ACTIVE_ORGANIZATION_STORAGE_KEY, organizationId.trim());
    } else {
      window.sessionStorage.removeItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures in restricted browser contexts.
  }
}

// ─── Axios instance ────────────────────────────────────────────────────────────

const BASE_URL = readRuntimeConfig('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL ?? '');

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 15_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// ─── Request interceptor — attach Bearer token ─────────────────────────────────

apiClient.interceptors.request.use((config) => {
  const token = _getToken?.();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const requestUrl = typeof config.url === 'string' ? config.url : '';
  const shouldAttachActiveOrganization = ![
    '/v1/auth/me',
    '/v1/auth/organizations',
    '/v1/auth/applications',
    '/v1/auth/active-organization',
  ].some((path) => requestUrl.endsWith(path));

  const organizationId = shouldAttachActiveOrganization ? readActiveOrganizationId() : null;
  if (organizationId) {
    config.headers['x-organization-id'] = organizationId;
  }

  return config;
});

// ─── Response interceptor — normalise errors to ApiError ──────────────────────

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiProblemDetails>) => {
    const payload = error.response?.data;
    const status = error.response?.status ?? 0;
    notifyAuthenticationRequired(status);

    if (payload && typeof payload === 'object' && 'status' in payload) {
      throw new ApiError(status, payload.code, payload.detail, payload);
    }

    // Network / timeout errors
    throw new ApiError(status, 'NETWORK_ERROR', error.message, {
      type: 'about:blank',
      title: 'Network Error',
      status,
      detail: error.message,
      correlationId: '',
    });
  },
);
